import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Checkbox,
  Modal,
  createTheme,
  ThemeProvider,
  IconButton,
  Button,
  Pagination,
  CircularProgress,
} from "@mui/material";
import "@fontsource/manrope";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CloseIcon from "@mui/icons-material/Close";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "4px",
        },
      },
    },
  },
});

const FeatureSelection = ({
  featuresLoading,
  featuresList,
  setFeaturesList,
  selectedFeaturesList,
  setSelectedFeaturesList,
  setSelectedParent,
  setImportPageOpen,
}) => {
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(true);

  const storiesPerPage = 6;
  const pageCount = Math.ceil(featuresList.length / storiesPerPage);

  const handleStorySelect = (story, event) => {
    event.stopPropagation();
    setSelectedFeatures((prev) =>
      prev.includes(story.id)
        ? prev.filter((id) => id !== story.id)
        : [...prev, story.id]
    );
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleCloseModal = () => {
    setImportPageOpen(false);
  };

  const handleBack = () => {
    setSelectedParent(null);
    setFeaturesList([]);
  };

  const handleSelectedFeatures = () => {
    setSelectedFeaturesList(selectedFeatures);
  };

  const handleSelectAll = () => {
    setSelectedFeatures(featuresList.map((story) => story.id));
  };

  const handleDeselectAll = () => {
    setSelectedFeatures([]);
  };

  const displayedStories = featuresList.slice(
    (page - 1) * storiesPerPage,
    page * storiesPerPage
  );

  return (
    <ThemeProvider theme={theme}>
      <Modal open={isModalOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "510px",
            height: "540px",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            px: 3,
            py: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            outline: "none",
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderBottom: 0,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleBack}
                sx={{ ml: -3, mr: 0.5 }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: "0.8rem" }} />
              </IconButton>
              <Typography variant="h6" sx={{ fontSize: "1.1rem", ml: -1 }}>
                Import User Stories
              </Typography>
            </Box>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, flexGrow: 1, overflowY: "auto" }}>
            <Typography
              variant="subtitle1"
              sx={{ fontSize: "0.9rem", mt: -2.5 }}
              gutterBottom
            >
              Epic title will be displayed here
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb:1, fontSize: "0.8rem", mt: 1 }}
            >
              Select a feature to move forward
            </Typography>

            <Box
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
              marginBottom={0.5}
            >
              <Button onClick={handleSelectAll} sx={{ textTransform: "none" }}>
                Select all
              </Button>
              <Button
                onClick={handleDeselectAll}
                sx={{ textTransform: "none" }}
              >
                Deselect all
              </Button>
            </Box>

            {displayedStories.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1.5,
                }}
              >
                {displayedStories.map((story) => (
                  <Card
                    key={story.id}
                    variant="outlined"
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      minHeight: "80px",
                      width: "210px",
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                      },
                      display: "flex",
                      flexDirection: "column",
                      padding: "8px",
                    }}
                    onClick={(e) => handleStorySelect(story, e)}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        checked={selectedFeatures.includes(story.id)}
                        onChange={(e) => handleStorySelect(story, e)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                      <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
                        {story.title}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, ml: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        Status: {story.status}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        Last updated: {story.lastUpdated}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                {featuresLoading ? (
                  <CircularProgress />
                ) : (
                  <Typography>No features found</Typography>
                )}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "relative",
              top: -10,
            }}
          >
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
              size="small"
            />
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: 1, ml: 45 }}>
              <Button
                onClick={handleCloseModal}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={selectedFeatures.length === 0}
                onClick={handleSelectedFeatures}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Next
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default FeatureSelection;
